import { useMemo, useCallback } from 'react';
import { StepProps } from '@/types/consultant-registration.types';
import { FormField } from '@/components/broker-registration/FormField';
import { TextInput } from '@/components/broker-registration/TextInput';
import { SelectInput } from '@/components/broker-registration/SelectInput';
import { CheckboxGroup } from '@/components/broker-registration/CheckboxGroup';
import { AddressAutocomplete } from '@/components/broker-registration/AddressAutocomplete';
import { PasswordInput } from '@/components/shared-registration/PasswordInput';
import { ALL_LANGUAGES, INSTITUTION_TYPES, HOW_HEARD_OPTIONS } from '@/constants/consultant-registration.constants';
import { useLanguage, type Language } from '@/contexts/LanguageContext';

export function StepOne({ formData, updateFormData, errors, clearError }: StepProps) {
  const { t, language } = useLanguage();

  // Filter out mother tongue from spoken languages options
  const availableSpokenLanguages = useMemo(() => {
    return ALL_LANGUAGES.filter((lang) => lang !== formData.motherTongue).map((lang) => ({
      value: lang,
      label: t(`consultant_registration.options.languages.${lang}`),
    }));
  }, [formData.motherTongue, t]);

  const languageOptions = ALL_LANGUAGES.map((lang) => ({
    value: lang,
    label: t(`consultant_registration.options.languages.${lang}`),
  }));

  const howHeardOptions = HOW_HEARD_OPTIONS[language].map((option) => ({
    value: option,
    label: option,
  }));

  const handleAddressSelect = useCallback(
    (components: {
      streetAddress: string;
      city: string;
      stateProvince: string;
      zipPostalCode: string;
      country: string;
    }) => {
      updateFormData({
        streetAddress: components.streetAddress,
        city: components.city,
        stateProvince: components.stateProvince,
        zipPostalCode: components.zipPostalCode,
        country: components.country,
      });
      clearError('streetAddress');
      clearError('city');
      clearError('stateProvince');
      clearError('zipPostalCode');
      clearError('country');
    },
    [updateFormData, clearError]
  );

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold border-b border-gray-700 pb-2 text-white">
        {t('consultant_registration.step_one.title')}
      </h2>

      {/* Personal Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white">
          {t('consultant_registration.step_one.personal_info')}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label={language === 'fr' ? 'Prénom' : 'First Name'} error={errors.firstName} required>
            <TextInput
              value={formData.firstName}
              onChange={(value) => {
                updateFormData({ firstName: value });
                clearError('firstName');
              }}
              placeholder={t('consultant_registration.step_one.first_name_placeholder')}
              error={!!errors.firstName}
            />
          </FormField>

          <FormField label={language === 'fr' ? 'Nom' : 'Last Name'} error={errors.lastName} required>
            <TextInput
              value={formData.lastName}
              onChange={(value) => {
                updateFormData({ lastName: value });
                clearError('lastName');
              }}
              placeholder={t('consultant_registration.step_one.last_name_placeholder')}
              error={!!errors.lastName}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label={t('consultant_registration.step_one.email')} error={errors.email} required>
            <TextInput
              type="email"
              value={formData.email}
              onChange={(value) => {
                updateFormData({ email: value });
                clearError('email');
              }}
              placeholder={t('consultant_registration.step_one.email_placeholder')}
              error={!!errors.email}
            />
          </FormField>

          <FormField label={t('consultant_registration.step_one.phone')} error={errors.phone} required>
            <TextInput
              type="tel"
              value={formData.phone}
              onChange={(value) => {
                updateFormData({ phone: value });
                clearError('phone');
              }}
              placeholder={t('consultant_registration.step_one.phone_placeholder')}
              error={!!errors.phone}
            />
          </FormField>
        </div>
      </div>

{/* NOUVEAU — Type d'entité (détermine le workflow de vérification Didit) */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-t border-gray-700 pt-4">
          {language === 'fr' ? "Type d'inscription" : 'Registration Type'}
        </h3>
        <p className="text-sm text-gray-400">
          {language === 'fr'
            ? 'Vous inscrivez-vous à titre personnel ou au nom d\'une entreprise?'
            : 'Are you registering as an individual or on behalf of a business?'}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div
            onClick={() => updateFormData({ entityType: 'individual' })}
            className={`cursor-pointer p-4 text-center rounded-lg border transition-all text-sm font-medium ${
              formData.entityType === 'individual'
                ? 'bg-[#CEAB65] text-[#1D314D] border-[#CEAB65]'
                : 'bg-gray-700/50 text-white border-gray-600 hover:bg-gray-700'
            }`}
          >
            {language === 'fr' ? 'Individu' : 'Individual'}
          </div>
          <div
            onClick={() => updateFormData({ entityType: 'business' })}
            className={`cursor-pointer p-4 text-center rounded-lg border transition-all text-sm font-medium ${
              formData.entityType === 'business'
                ? 'bg-[#CEAB65] text-[#1D314D] border-[#CEAB65]'
                : 'bg-gray-700/50 text-white border-gray-600 hover:bg-gray-700'
            }`}
          >
            {language === 'fr' ? 'Entreprise' : 'Business'}
          </div>
        </div>
      </div>

      {/* Institution Types */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-t border-gray-700 pt-4">
          {t('consultant_registration.step_one.institution_types')}
        </h3>
        <p className="text-sm text-gray-400">
          {t('consultant_registration.step_one.institution_types_hint')}
        </p>

        <div
          className="space-y-3 max-h-96 overflow-y-auto border border-gray-600 rounded-lg p-4 bg-gray-700/50"
          data-error={!!errors.institutionTypes}
        >
          {INSTITUTION_TYPES.map((category) => (
            <div key={category.category} className="mb-4">
              <h4 className="text-sm font-semibold text-[#CEAB65] mb-2">
                {t(`consultant_registration.options.institution_categories.${category.category}`)}
              </h4>
              <div className="grid grid-cols-1 gap-2 pl-2">
                {category.values.map((institution) => (
                  <label
                    key={institution}
                    className="flex items-start gap-2 text-sm text-white/95 cursor-pointer hover:bg-white/5 p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={formData.institutionTypes.includes(institution)}
                      onChange={(e) => {
                        const newTypes = e.target.checked
                          ? [...formData.institutionTypes, institution]
                          : formData.institutionTypes.filter((t) => t !== institution);
                        updateFormData({ institutionTypes: newTypes });
                        clearError('institutionTypes');
                      }}
                      className="mt-0.5 rounded text-[#CEAB65] flex-shrink-0"
                    />
                    <span>{t(`consultant_registration.options.institution_types.${institution}`)}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        {errors.institutionTypes && (
          <p className="text-sm text-red-400">{errors.institutionTypes}</p>
        )}
      </div>

      {/* How Did You Hear About Us */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-t border-gray-700 pt-4">
          {language === 'fr' ? 'Comment nous avez-vous connu ?' : 'How Did You Hear About Us?'}
        </h3>

        <FormField
          label={language === 'fr' ? 'Comment avez-vous entendu parler d\'AcquizGroup ?' : 'How did you hear about AcquizGroup?'}
          error={errors.howHeard}
          required
        >
          <SelectInput
            value={formData.howHeard}
            onChange={(value) => {
              updateFormData({ howHeard: value });
              clearError('howHeard');
            }}
            options={[
              { value: '', label: language === 'fr' ? 'Sélectionnez une option' : 'Select an option' },
              ...howHeardOptions,
            ]}
            error={!!errors.howHeard}
          />
        </FormField>
      </div>

      {/* Languages */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-t border-gray-700 pt-4">
          {t('consultant_registration.step_one.languages')}
        </h3>

        <FormField
          label={t('consultant_registration.step_one.mother_tongue')}
          error={errors.motherTongue}
          required
        >
          <SelectInput
            value={formData.motherTongue}
            onChange={(value) => {
              updateFormData({ motherTongue: value });
              clearError('motherTongue');
            }}
            options={[
              { value: '', label: t('consultant_registration.step_one.mother_tongue_placeholder') },
              ...languageOptions,
            ]}
            error={!!errors.motherTongue}
          />
        </FormField>

        <FormField label={t('consultant_registration.step_one.spoken_languages')}>
          <p className="text-xs text-gray-400 mb-2">
            {t('consultant_registration.step_one.spoken_languages_hint')}
          </p>
          <CheckboxGroup
            options={availableSpokenLanguages}
            values={formData.spokenLanguages}
            onChange={(values) => updateFormData({ spokenLanguages: values })}
          />
        </FormField>
      </div>

      {/* Current Residential Address - Merged autocomplete field */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-t border-gray-700 pt-4">
          {t('consultant_registration.step_one.address_title')}
        </h3>
        <p className="text-sm text-gray-400">
          {t('consultant_registration.step_one.address_subtitle')}
        </p>

        <FormField
          label={t('consultant_registration.step_one.street_address')}
          error={errors.streetAddress}
          required
        >
          <AddressAutocomplete
            value={formData.streetAddress}
            onChange={(value) => {
              updateFormData({ streetAddress: value });
              clearError('streetAddress');
            }}
            onAddressSelect={handleAddressSelect}
            placeholder={t('consultant_registration.step_one.address_search_placeholder')}
            error={!!errors.streetAddress}
          />
          <p className="text-xs text-gray-400 mt-1">
            {t('consultant_registration.step_one.address_search_hint')}
          </p>
        </FormField>

        <div className="grid gap-4 p-4 border border-gray-600 rounded-lg bg-gray-700/50">
          <div className="grid grid-cols-2 gap-4">
            <FormField label={t('consultant_registration.step_one.city')} error={errors.city} required>
              <input
                type="text"
                value={formData.city}
                readOnly
                placeholder={t('consultant_registration.step_one.city_placeholder')}
                className={`w-full rounded-md border ${errors.city ? 'border-red-500' : 'border-gray-600'} bg-gray-600 px-4 py-2 text-white placeholder-gray-400 cursor-not-allowed`}
              />
            </FormField>

            <FormField
              label={t('consultant_registration.step_one.state_province')}
              error={errors.stateProvince}
              required
            >
              <input
                type="text"
                value={formData.stateProvince}
                readOnly
                placeholder={t('consultant_registration.step_one.state_province_placeholder')}
                className={`w-full rounded-md border ${errors.stateProvince ? 'border-red-500' : 'border-gray-600'} bg-gray-600 px-4 py-2 text-white placeholder-gray-400 cursor-not-allowed`}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              label={t('consultant_registration.step_one.zip_postal_code')}
              error={errors.zipPostalCode}
              required
            >
              <input
                type="text"
                value={formData.zipPostalCode}
                readOnly
                placeholder={t('consultant_registration.step_one.zip_postal_code_placeholder')}
                className={`w-full rounded-md border ${errors.zipPostalCode ? 'border-red-500' : 'border-gray-600'} bg-gray-600 px-4 py-2 text-white placeholder-gray-400 cursor-not-allowed`}
              />
            </FormField>

            <FormField label={t('consultant_registration.step_one.country')} error={errors.country} required>
              <input
                type="text"
                value={formData.country}
                readOnly
                placeholder={t('consultant_registration.step_one.country_placeholder')}
                className={`w-full rounded-md border ${errors.country ? 'border-red-500' : 'border-gray-600'} bg-gray-600 px-4 py-2 text-white placeholder-gray-400 cursor-not-allowed`}
              />
            </FormField>
          </div>
        </div>
      </div>

      {/* Account Security */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-t border-gray-700 pt-4">
          {t('consultant_registration.step_one.account_security')}
        </h3>

        <FormField label={t('consultant_registration.step_one.password')} error={errors.password} required>
          <PasswordInput
            value={formData.password}
            onChange={(value) => {
              updateFormData({ password: value });
              clearError('password');
            }}
            placeholder={t('consultant_registration.step_one.password_placeholder')}
            error={!!errors.password}
            autoComplete="new-password"
          />
        </FormField>

        <FormField
          label={t('consultant_registration.step_one.confirm_password')}
          error={errors.confirmPassword}
          required
        >
          <PasswordInput
            value={formData.confirmPassword}
            onChange={(value) => {
              updateFormData({ confirmPassword: value });
              clearError('confirmPassword');
            }}
            placeholder={t('consultant_registration.step_one.confirm_password_placeholder')}
            error={!!errors.confirmPassword}
            autoComplete="new-password"
          />
        </FormField>
      </div>
    </div>
  );
}
